/**
 * useFormSheetUrlRouting — F-Refinement-9 / Wave 2.5
 *
 * Encapsulates the URL deep-link → sheet-open pattern used across ROROS pages
 * (AiOperatorsPage, GoalsPage, OutcomesPage). Reads ?urlKey=<id> from the URL,
 * resolves the matching item from `items`, and guards against re-opening after
 * the consumer has consumed the link.
 *
 * Typical usage:
 *
 *   const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
 *     urlKey: 'edit-goal',
 *     items: goals,
 *     getId: (g) => g.id,
 *   });
 *
 *   useEffect(() => {
 *     if (!deepLinkTarget) return;
 *     setEditingGoal(deepLinkTarget);
 *     setIsFormOpen(true);
 *     consumeDeepLink();
 *   }, [deepLinkTarget]);
 *
 * Behaviour contract:
 *  - deepLinkTarget: the resolved item matching the URL param, or null when the
 *    param is absent / invalid / already consumed.
 *  - consumeDeepLink: call after opening the sheet. Clears deepLinkTarget state
 *    AND sets the consumed ref so subsequent effect fires (e.g. from React Query
 *    cache invalidation re-rendering items) do not re-open the sheet.
 *  - Invalid IDs → graceful no-op (deepLinkTarget stays null).
 *  - Items array not yet loaded → waits (no-op until items is non-empty).
 *  - URL param cleared (sheet closed by FormSheet) → deepLinkTarget resets to null.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Module-scope default so it is referentially stable across renders.
// Defined here (not inside the hook) to ensure the dep-array eslint-disable
// comment below is never a footgun: if getId were recreated every render,
// adding it to deps would cause infinite re-fires. The module-scope definition
// makes "always stable" factually true for the default case.
function defaultGetId(item: unknown): string {
  return (item as { id: string }).id;
}

export function useFormSheetUrlRouting<T>(args: {
  urlKey: string;
  items: T[] | undefined;
  getId?: (item: T) => string;
}): {
  deepLinkTarget: T | null;
  consumeDeepLink: () => void;
} {
  const { urlKey, items, getId = defaultGetId as (item: T) => string } = args;
  const [searchParams] = useSearchParams();
  const deepLinkId = searchParams.get(urlKey);

  // Guard against re-opening after consumeDeepLink() has been called.
  const consumedRef = useRef<string | null>(null);
  const [deepLinkTarget, setDeepLinkTarget] = useState<T | null>(null);

  useEffect(() => {
    if (!deepLinkId) {
      // URL param cleared (sheet closed via FormSheet URL sync) → clear state.
      setDeepLinkTarget(null);
      return;
    }
    // Ref guard: already consumed (user opened then closed the sheet) → no-op.
    if (consumedRef.current === deepLinkId) return;
    // Data not ready yet → wait.
    if (!items || items.length === 0) return;
    const found = items.find((item) => getId(item) === deepLinkId);
    // Invalid ID → graceful no-op; never crash.
    if (!found) return;
    // Mark as consumed immediately to prevent duplicate fires on items re-fetch.
    consumedRef.current = deepLinkId;
    setDeepLinkTarget(found);
  // NOTE: The default `(item) => item.id` is always stable (defined at module
  // scope, outside the hook — see `defaultGetId` above). If you supply a custom
  // `getId`, it MUST be a stable reference — defined outside the component or
  // memoized with `useCallback`. If getId changes every render, this effect
  // re-fires on every render, causing infinite deep-link re-opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId, items]);

  /**
   * Call after opening the sheet. Sets the consumed ref AND clears deepLinkTarget
   * state so that subsequent effect fires triggered by React Query cache
   * invalidation (e.g. items or related queries re-fetching after save) cannot
   * read a stale non-null deepLinkTarget and re-open the sheet.
   */
  const consumeDeepLink = useCallback(() => {
    if (deepLinkId) consumedRef.current = deepLinkId;
    setDeepLinkTarget(null); // ← prevents stale-state re-open after cache invalidation
  }, [deepLinkId]);

  return { deepLinkTarget, consumeDeepLink };
}
